import React from "react";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  CHECKOUT_BACK_LABEL,
  CHECKOUT_PRIMARY_CTA_CLASS,
} from "./checkoutPaymentLabels";

interface CheckoutActionBarProps {
  finalPriceLabel: string;
  primaryLabel: string;
  primaryDisabled: boolean;
  primaryLoading: boolean;
  waiting: boolean;
  showBack: boolean;
  onBack: () => void;
  onPrimary: () => void;
}

export const CheckoutActionBar: React.FC<CheckoutActionBarProps> = ({
  finalPriceLabel,
  primaryLabel,
  primaryDisabled,
  primaryLoading,
  waiting,
  showBack,
  onBack,
  onPrimary,
}) => {
  const barRef = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return;

    const applyHeight = () => {
      document.documentElement.style.setProperty(
        "--checkout-action-bar-h",
        `${Math.ceil(el.getBoundingClientRect().height)}px`
      );
    };

    applyHeight();
    const observer = new ResizeObserver(applyHeight);
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--checkout-action-bar-h");
    };
  }, []);

  return (
    <div
      ref={barRef}
      id="checkout-action-bar"
      role="region"
      aria-label="Ações do checkout"
      className="fixed bottom-0 left-20 right-0 z-40 border-t border bg-background/95 px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] backdrop-blur pb-[max(0.75rem,env(safe-area-inset-bottom))]"
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-3 sm:flex-row sm:items-center">
        <div className="min-w-0 shrink-0">
          <p className="text-xs text-text-secondary">Total do pedido</p>
          <p className="text-lg font-semibold tabular-nums text-success-subtle">
            {finalPriceLabel}
          </p>
        </div>

        <div className="flex min-w-0 w-full items-center gap-3 sm:ml-auto sm:flex-1 sm:justify-end">
          {showBack ? (
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              className="min-h-[44px] min-w-[5.5rem] shrink-0"
            >
              {CHECKOUT_BACK_LABEL}
            </Button>
          ) : null}

          {waiting ? (
            <div
              className="flex min-h-[44px] min-w-0 flex-1 items-center justify-center gap-2 rounded-md border border-success bg-success-muted px-3 text-sm font-medium text-success-subtle sm:flex-none sm:min-w-[16rem]"
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              <LoadingSpinner size="sm" />
              <span className="truncate">{primaryLabel}</span>
            </div>
          ) : (
            <Button
              id="checkout-action-bar-primary"
              type="button"
              onClick={onPrimary}
              disabled={primaryDisabled}
              isLoading={primaryLoading}
              className={`min-h-[44px] min-w-0 flex-1 font-medium sm:flex-none sm:min-w-[16rem] ${CHECKOUT_PRIMARY_CTA_CLASS}`}
            >
              {primaryLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
